import { DecimalPipe } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { WidgetService } from '../../../services/builder/widget.service';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import {
  defaultMultiplierOption,
  isDefaultMultiplierOption,
  MultiplierOption,
  MultiplierRadioButtonCustomization,
  multiplierWidgetWeight,
} from './multiplier-answer';

type OptionFormGroup = FormGroup<{
  key: FormControl<string>;
  label: FormControl<string | null>;
  multiplier: FormControl<number | null>;
  min: FormControl<number | null>;
  max: FormControl<number | null>;
  isDefault: FormControl<boolean>;
}>;

const OPTION_KEY_PREFIX = 'option_';

@Component({
  selector: 'lib-multiplier-radiobutton-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      [form]="customizationForm"
      [confirmDisabled]="customizationForm.controls.options.invalid"
      [hideAutocompletion]="true"
      header="Personalizzazione Radio con moltiplicatore"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div
          [formGroup]="customizationForm"
          class="mlt-panel flex flex-col gap-4 text-sm"
        >
          <div class="flex flex-col gap-1">
            <label for="desc" class="text-slate-600">Descrizione</label>
            <textarea
              id="desc"
              pTextarea
              rows="2"
              formControlName="description"
            ></textarea>
          </div>

          <div class="flex items-center justify-between">
            <label for="decimal" class="text-slate-600">Valori decimali</label>
            <p-checkbox
              inputId="decimal"
              formControlName="decimal"
              [binary]="true"
            ></p-checkbox>
          </div>

          <div class="flex flex-col gap-2">
            <span class="font-semibold text-slate-700">Opzioni</span>
            <p-table
              [value]="customizationForm.controls.options.controls"
              class="p-datatable-sm"
              tableStyleClass="p-fluid"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th>Descrizione</th>
                  <th class="w-32">Moltiplicatore</th>
                  <th class="w-28">Min</th>
                  <th class="w-28">Max</th>
                  <th class="w-10 text-center">
                    <p-button
                      icon="pi pi-plus"
                      [text]="true"
                      [rounded]="true"
                      size="small"
                      severity="secondary"
                      ariaLabel="Aggiungi opzione"
                      (onClick)="addOption()"
                    />
                  </th>
                </tr>
              </ng-template>
              <ng-template
                pTemplate="body"
                let-optionCtrl
                let-rowIndex="rowIndex"
              >
                <tr [formGroup]="optionCtrl">
                  <td>
                    <input
                      pInputText
                      pSize="small"
                      placeholder="Descrizione"
                      formControlName="label"
                    />
                  </td>
                  <td>
                    <p-input-number
                      [autofocus]="false"
                      formControlName="multiplier"
                      mode="decimal"
                      [min]="0"
                      [minFractionDigits]="0"
                      [maxFractionDigits]="3"
                      size="small"
                    />
                  </td>
                  <td>
                    <p-input-number
                      [autofocus]="false"
                      formControlName="min"
                      mode="decimal"
                      [min]="0"
                      [minFractionDigits]="0"
                      [maxFractionDigits]="fractionDigits()"
                      size="small"
                    />
                  </td>
                  <td>
                    <p-input-number
                      [autofocus]="false"
                      formControlName="max"
                      mode="decimal"
                      [min]="0"
                      [minFractionDigits]="0"
                      [maxFractionDigits]="fractionDigits()"
                      size="small"
                    />
                  </td>
                  <td class="text-center">
                    @if (!optionCtrl.controls.isDefault.value) {
                      <p-button
                        icon="pi pi-times"
                        [text]="true"
                        [rounded]="true"
                        size="small"
                        severity="secondary"
                        ariaLabel="Rimuovi opzione"
                        (onClick)="removeOption(rowIndex)"
                      />
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>

            @if (hasInvalidOption()) {
              <small class="text-red-500">
                Ogni opzione richiede una descrizione; oltre a quella
                predefinita servono anche un moltiplicatore maggiore di 0 con al massimo 3
                decimali, un minimo e un massimo con 0 ≤ min ≤ max: interi, o
                con al massimo 3 decimali se i valori sono decimali.
              </small>
            }
            @if (
              customizationForm.controls.options.hasError('duplicateLabels')
            ) {
              <small class="text-red-500">
                Le descrizioni delle opzioni devono essere tutte diverse.
              </small>
            }
            <span class="text-slate-600">
              Peso del widget: {{ weight() | number: '1.0-3' }}
            </span>
          </div>

          <div class="flex items-center justify-between">
            <label for="required" class="text-slate-600">
              Campo obbligatorio
            </label>
            <p-checkbox
              id="required"
              formControlName="required"
              [binary]="true"
            ></p-checkbox>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    ReactiveFormsModule,
    DecimalPipe,
    baseCustomPanelStructureComponent,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    CheckboxModule,
    TableModule,
    TextareaModule,
  ],
  styles: [
    `
      /* Tighter, vertically-centered table cells */
      .mlt-panel ::ng-deep .p-datatable-sm .p-datatable-thead > tr > th,
      .mlt-panel ::ng-deep .p-datatable-sm .p-datatable-tbody > tr > td {
        padding: 0.3rem 0.4rem;
        vertical-align: middle;
      }
      .mlt-panel ::ng-deep .p-datatable-sm .p-datatable-thead > tr > th {
        color: #64748b;
        font-weight: 600;
      }
    `,
  ],
})
export class MultiplierRadioButtonCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(
    WidgetService<MultiplierRadioButtonCustomization>,
  );
  protected uniqueIdService = inject(UniqueIdService);

  customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    decimal: [false],
    options: this.fb.array<OptionFormGroup>([], [uniqueLabelsValidator]),
    required: [false],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
  });

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      const uniqueId = this.widgetService.uniqueId;
      if (!uniqueId) return;

      // The default option is options[0]; a widget saved without one gets a
      // fresh one in front, and keeps every option it had.
      const options: MultiplierOption[] = widget.customization.options ?? [];
      const [first, ...rest] = options;
      const hasDefault = !!first && isDefaultMultiplierOption(first);
      this.customizationForm.controls.options.clear();
      this.pushDefaultOption(hasDefault ? first : defaultMultiplierOption());
      (hasDefault ? rest : options).forEach((option) =>
        this.pushOption(option),
      );

      this.customizationForm.patchValue({
        id: uniqueId,
        description: widget.customization.description,
        decimal: widget.customization.decimal ?? false,
        required: widget.customization.required,
        showIfRule: widget.customization.showIfRule,
      });
    });

    this.customizationForm.controls.decimal.valueChanges.subscribe(
      (decimal) => {
        for (const option of this.customizationForm.controls.options.controls) {
          if (option.controls.isDefault.value) continue;
          // Integer mode cannot keep fractional bounds.
          if (!decimal) {
            const { min, max } = option.controls;
            if (min.value != null) min.setValue(Math.round(min.value));
            if (max.value != null) max.setValue(Math.round(max.value));
          }
          // The bounds rule depends on the mode.
          option.updateValueAndValidity();
        }
      },
    );
  }

  /**
   * `0 ≤ min ≤ max`, whole numbers in integer mode, at most 3 decimals
   * otherwise. A blank bound is `required`'s to report: `0 <= null` is true in
   * JavaScript, so nulls are left out here rather than compared.
   */
  private readonly boundsValidator = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const { min, max } = group.getRawValue() as {
      min: number | null;
      max: number | null;
    };
    if (min != null && max != null && min > max) return { minAboveMax: true };

    const decimal = !!this.customizationForm.controls.decimal.value;
    const fits = (bound: number | null) =>
      bound == null ||
      (decimal ? hasAtMostThreeDecimals(bound) : Number.isInteger(bound));
    return fits(min) && fits(max) ? null : { boundsPrecision: true };
  };

  protected fractionDigits(): number {
    return this.customizationForm.controls.decimal.value ? 3 : 0;
  }

  /** The widget weight as the form stands, same rule as the points calculation. */
  protected weight(): number {
    return multiplierWidgetWeight(
      this.customizationForm.controls.options.getRawValue(),
    );
  }

  protected hasInvalidOption(): boolean {
    return this.customizationForm.controls.options.controls.some(
      (option) => option.invalid,
    );
  }

  addOption() {
    const options = this.customizationForm.controls.options.controls;
    const labels = new Set(options.map((o) => o.controls.label.value?.trim()));
    let next = options.length;
    while (labels.has(`Opzione ${next}`)) next++;
    this.pushOption({
      // Random, not a counter: the saved options cannot tell which keys removed
      // options had, and a saved answer may still point at one of them.
      key: `${OPTION_KEY_PREFIX}${crypto.randomUUID().slice(0, 8)}`,
      label: `Opzione ${next}`,
      multiplier: 1,
      min: 0,
      max: 10,
    });
  }

  removeOption(index: number) {
    const option = this.customizationForm.controls.options.at(index);
    if (!option || option.controls.isDefault.value) return;
    this.customizationForm.controls.options.removeAt(index);
  }

  /** Worth nothing, never removed: only its label is the author's. */
  private pushDefaultOption(option: MultiplierOption) {
    this.customizationForm.controls.options.push(
      this.fb.group({
        key: this.fb.nonNullable.control(option.key),
        label: this.fb.control<string | null>(option.label, [
          Validators.required,
          notBlankValidator,
        ]),
        multiplier: this.fb.control<number | null>({
          value: 0,
          disabled: true,
        }),
        min: this.fb.control<number | null>({ value: null, disabled: true }),
        max: this.fb.control<number | null>({ value: null, disabled: true }),
        isDefault: this.fb.nonNullable.control(true),
      }),
    );
  }

  private pushOption(option: MultiplierOption) {
    this.customizationForm.controls.options.push(
      this.fb.group(
        {
          key: this.fb.nonNullable.control(option.key),
          label: this.fb.control<string | null>(option.label, [
            Validators.required,
            notBlankValidator,
          ]),
          multiplier: this.fb.control<number | null>(option.multiplier, [
            Validators.required,
            positiveValidator,
            atMostThreeDecimalsValidator,
          ]),
          // Blank stays blank: a missing min must be filled in, not guessed.
          min: this.fb.control<number | null>(option.min ?? null, [
            Validators.required,
            Validators.min(0),
          ]),
          max: this.fb.control<number | null>(option.max ?? null, [
            Validators.required,
            Validators.min(0),
          ]),
          isDefault: this.fb.nonNullable.control(false),
        },
        { validators: [this.boundsValidator] },
      ),
    );
  }

  /** Only valid rows get here (see `updateWidget`): their bounds are set. */
  private toOption(optionCtrl: OptionFormGroup): MultiplierOption {
    const { key, label, multiplier, min, max, isDefault } =
      optionCtrl.getRawValue();

    if (isDefault) return defaultMultiplierOption((label ?? '').trim());

    return {
      key,
      label: (label ?? '').trim(),
      multiplier: multiplier ?? 0,
      // Never a blank bound turned into 0: an unset one stays unset.
      min: min ?? undefined,
      max: max ?? undefined,
    };
  }

  updateWidget(closed: boolean) {
    if (!closed) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    // "Conferma" is already disabled meanwhile; this guards the save itself.
    if (this.customizationForm.controls.options.invalid) return;

    const formValue = this.customizationForm.value;
    const id = formValue.id?.trim();
    if (!id) return;

    this.widgetService.updateWidget(id, {
      description: formValue.description ?? '',
      decimal: formValue.decimal ?? false,
      options: this.customizationForm.controls.options.controls.map((ctrl) =>
        this.toOption(ctrl),
      ),
      required: formValue.required ?? false,
      showIfRule: formValue.showIfRule || '',
      // An `{ option, value }` answer has nothing a computed value could fill.
      autocompleteRule: '',
    });
    this.widgetService.settingVisibility.set(false);
  }
}

function notBlankValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string | null;
  return value != null && value.trim() === '' ? { blank: true } : null;
}

function positiveValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as number | null;
  return value != null && value <= 0 ? { positive: true } : null;
}

function atMostThreeDecimalsValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value as number | null;
  return value == null || hasAtMostThreeDecimals(value)
    ? null
    : { decimals: true };
}

function hasAtMostThreeDecimals(value: number): boolean {
  const thousandths = value * 1000;
  return Math.abs(thousandths - Math.round(thousandths)) < 1e-6;
}

/** Labels are what the AI compile and the coherence check tell options apart by. */
function uniqueLabelsValidator(
  array: AbstractControl,
): ValidationErrors | null {
  const labels = (array as FormArray<OptionFormGroup>)
    .getRawValue()
    .map((option) => (option.label ?? '').trim().toLowerCase())
    .filter((label) => label !== '');
  return new Set(labels).size !== labels.length
    ? { duplicateLabels: true }
    : null;
}
